/**
 * Gallery Lightbox Functionality
 * Vanilla JS implementation for NXT CPT Swiper gallery template
 * Displays images in 4:5 aspect ratio with content below
 * Supports keyboard navigation (Arrow Left/Right)
 */

(function() {
	'use strict';
	
	let currentLightboxData = null;
	let currentGallerySlides = [];
	let currentSlideIndex = -1;
	
	if (document.readyState === 'loading') {
		document.addEventListener('DOMContentLoaded', initGalleryLightbox);
	} else {
		initGalleryLightbox();
	}
	
	function initGalleryLightbox() {
		setupAllSlides();
		
		const observer = new MutationObserver(function(mutations) {
			mutations.forEach(function(mutation) {
				mutation.addedNodes.forEach(function(node) {
					if (node.nodeType === 1) {
						if (node.classList && node.classList.contains('swiper-slide-gallery')) {
							setupSlideEvents(node);
						}
						const gallerySlides = node.querySelectorAll ? node.querySelectorAll('.swiper-slide-gallery') : [];
						gallerySlides.forEach(slide => {
							setupSlideEvents(slide);
						});
					}
				});
			});
		});
		
		observer.observe(document.body, {
			childList: true,
			subtree: true
		});
		
		document.addEventListener('keydown', function(e) {
			if (e.key === 'Escape') {
				closeAllLightboxes();
			}
			
			if (e.key === 'ArrowLeft') {
				navigateToPrevious();
			}
			
			if (e.key === 'ArrowRight') {
				navigateToNext();
			}
		});
	}
	
	function setupAllSlides() {
		const gallerySlides = document.querySelectorAll('.swiper-slide-gallery');
		
		gallerySlides.forEach(slide => {
			setupSlideEvents(slide);
		});
	}
	
	function setupSlideEvents(slide) {
		const slideId = slide.dataset.slideId;
		if (!slideId) {
			console.warn('Gallery slide missing slideId:', slide);
			return;
		}
		
		if (slide.dataset.lightboxInitialized === 'true') {
			return;
		}
		
		let dataScript = slide.querySelector('.gallery-lightbox-data[data-slide-id="' + slideId + '"]');
		
		if (!dataScript && slide.parentNode) {
			dataScript = slide.parentNode.querySelector('.gallery-lightbox-data[data-slide-id="' + slideId + '"]');
		}
		
		if (!dataScript) {
			const swiperSlide = slide.closest('.swiper-slide');
			if (swiperSlide) {
				dataScript = swiperSlide.querySelector('.gallery-lightbox-data[data-slide-id="' + slideId + '"]');
			}
		}
		
		if (!dataScript) {
			console.warn('Gallery slide missing data script:', slideId);
			return;
		}
		
		let lightboxData;
		try {
			lightboxData = JSON.parse(dataScript.textContent);
		} catch (e) {
			console.error('Failed to parse lightbox data:', e);
			return;
		}
		
		const triggers = slide.querySelectorAll('[data-lightbox-trigger="' + slideId + '"]');
		
		if (triggers.length === 0) {
			console.warn('No trigger elements found for slide:', slideId);
			return;
		}
		
		triggers.forEach(trigger => {
			trigger.addEventListener('click', function(e) {
				e.preventDefault();
				e.stopPropagation();
				
				const swiperContainer = slide.closest('.swiper-container, .nxt-swiper');
				const gallerySlides = swiperContainer ? Array.from(swiperContainer.querySelectorAll('.swiper-slide-gallery')) : [];
				const slideIndex = gallerySlides.indexOf(slide);
				
				openLightbox(lightboxData, gallerySlides, slideIndex);
			});
		});
		
		slide.dataset.lightboxInitialized = 'true';
	}
	
	function createLightboxElement(data) {
		const lightbox = document.createElement('div');
		lightbox.id = 'lightbox-' + data.slideId;
		lightbox.className = 'gallery-lightbox';
		lightbox.style.display = 'none';
		
		lightbox.innerHTML = `
			<button class="gallery-lightbox-close" aria-label="Close">&times;</button>
			
			<div class="gallery-lightbox-content">
				<div class="gallery-lightbox-image-wrapper">
					<img src="${escapeHtml(data.imageUrl)}" alt="${escapeHtml(data.title)}" class="gallery-lightbox-image" />
				</div>
				
				${data.content ? `<div class="gallery-lightbox-text">${unescapeHtml(data.content)}</div>` : ''}
			</div>
		`;
		
		return lightbox;
	}
	
	function escapeHtml(text) {
		const div = document.createElement('div');
		div.textContent = text;
		return div.innerHTML;
	}
	
	function unescapeHtml(text) {
		const div = document.createElement('div');
		div.innerHTML = text;
		return div.innerHTML;
	}
	
	function setupLightboxClose(lightbox) {
		const closeBtn = lightbox.querySelector('.gallery-lightbox-close');
		if (closeBtn) {
			closeBtn.addEventListener('click', function(e) {
				e.preventDefault();
				closeLightbox(lightbox);
			});
		}
		
		lightbox.addEventListener('click', function(e) {
			if (e.target === lightbox) {
				closeLightbox(lightbox);
			}
		});
		
		const content = lightbox.querySelector('.gallery-lightbox-content');
		if (content) {
			content.addEventListener('click', function(e) {
				e.stopPropagation();
			});
		}
	}
	
	function openLightbox(lightboxData, gallerySlides, slideIndex) {
		closeAllLightboxes();
		
		currentLightboxData = lightboxData;
		currentGallerySlides = gallerySlides || [];
		currentSlideIndex = slideIndex ?? -1;
		
		const lightbox = createLightboxElement(lightboxData);
		
		document.body.appendChild(lightbox);
		
		lightbox.style.display = 'flex';
		
		setTimeout(() => {
			lightbox.classList.add('gallery-lightbox-open');
		}, 10);
		
		document.body.style.overflow = 'hidden';
		
		setupLightboxClose(lightbox);
		
		const closeBtn = lightbox.querySelector('.gallery-lightbox-close');
		if (closeBtn) {
			closeBtn.focus();
		}
	}
	
	function navigateToNext() {
		if (currentGallerySlides.length === 0 || currentSlideIndex === -1) {
			return;
		}
		
		const nextIndex = (currentSlideIndex + 1) % currentGallerySlides.length;
		const nextSlide = currentGallerySlides[nextIndex];
		
		if (nextSlide) {
			const nextSlideId = nextSlide.dataset.slideId;
			const nextData = getSlideData(nextSlide, nextSlideId);
			
			if (nextData) {
				updateLightboxContent(nextData, nextIndex);
			}
		}
	}
	
	function navigateToPrevious() {
		if (currentGallerySlides.length === 0 || currentSlideIndex === -1) {
			return;
		}
		
		const prevIndex = currentSlideIndex === 0 ? currentGallerySlides.length - 1 : currentSlideIndex - 1;
		const prevSlide = currentGallerySlides[prevIndex];
		
		if (prevSlide) {
			const prevSlideId = prevSlide.dataset.slideId;
			const prevData = getSlideData(prevSlide, prevSlideId);
			
			if (prevData) {
				updateLightboxContent(prevData, prevIndex);
			}
		}
	}
	
	function getSlideData(slide, slideId) {
		let dataScript = slide.querySelector('.gallery-lightbox-data[data-slide-id="' + slideId + '"]');
		
		if (!dataScript && slide.parentNode) {
			dataScript = slide.parentNode.querySelector('.gallery-lightbox-data[data-slide-id="' + slideId + '"]');
		}
		
		if (!dataScript) {
			const swiperSlide = slide.closest('.swiper-slide');
			if (swiperSlide) {
				dataScript = swiperSlide.querySelector('.gallery-lightbox-data[data-slide-id="' + slideId + '"]');
			}
		}
		
		if (dataScript) {
			try {
				return JSON.parse(dataScript.textContent);
			} catch (e) {
				console.error('Failed to parse lightbox data:', e);
			}
		}
		
		return null;
	}
	
	function updateLightboxContent(newData, newIndex) {
		const lightbox = document.querySelector('.gallery-lightbox');
		if (!lightbox) return;
		
		const content = lightbox.querySelector('.gallery-lightbox-content');
		if (!content) return;
		
		content.classList.add('gallery-lightbox-transitioning');
		
		setTimeout(() => {
			const imageWrapper = content.querySelector('.gallery-lightbox-image-wrapper');
			const textWrapper = content.querySelector('.gallery-lightbox-text');
			
			if (imageWrapper) {
				const img = imageWrapper.querySelector('.gallery-lightbox-image');
				if (img) {
					img.src = newData.imageUrl;
					img.alt = newData.title || '';
				}
			}
			
			if (textWrapper && newData.content) {
				textWrapper.innerHTML = unescapeHtml(newData.content);
			} else if (textWrapper) {
				textWrapper.innerHTML = '';
			}
			
			currentLightboxData = newData;
			currentSlideIndex = newIndex;
			
			setTimeout(() => {
				content.classList.remove('gallery-lightbox-transitioning');
			}, 50);
		}, 150);
	}
	
	function closeLightbox(lightbox) {
		lightbox.classList.remove('gallery-lightbox-open');
		
		currentLightboxData = null;
		currentGallerySlides = [];
		currentSlideIndex = -1;
		
		setTimeout(() => {
			if (lightbox.parentNode) {
				lightbox.parentNode.removeChild(lightbox);
			}
		}, 300);
		
		document.body.style.overflow = '';
	}
	
	function closeAllLightboxes() {
		const openLightboxes = document.querySelectorAll('.gallery-lightbox');
		openLightboxes.forEach(lightbox => {
			closeLightbox(lightbox);
		});
	}
	
	window.NXTGalleryLightbox = {
		open: function(slideId) {
			const slide = document.querySelector('.swiper-slide-gallery[data-slide-id="' + slideId + '"]');
			if (slide) {
				let dataScript = slide.querySelector('.gallery-lightbox-data[data-slide-id="' + slideId + '"]');
				
				if (!dataScript && slide.parentNode) {
					dataScript = slide.parentNode.querySelector('.gallery-lightbox-data[data-slide-id="' + slideId + '"]');
				}
				
				if (!dataScript) {
					const swiperSlide = slide.closest('.swiper-slide');
					if (swiperSlide) {
						dataScript = swiperSlide.querySelector('.gallery-lightbox-data[data-slide-id="' + slideId + '"]');
					}
				}
				
				if (dataScript) {
					try {
						const lightboxData = JSON.parse(dataScript.textContent);
						openLightbox(lightboxData);
					} catch (e) {
						console.error('Failed to parse lightbox data:', e);
					}
				}
			}
		},
		
		close: function(slideId) {
			if (slideId) {
				const lightbox = document.getElementById('lightbox-' + slideId);
				if (lightbox) {
					closeLightbox(lightbox);
				}
			} else {
				closeAllLightboxes();
			}
		},
		
		closeAll: closeAllLightboxes
	};
	
})();
